import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertTriangle, Activity, Shield, CheckCircle,
  Clock, RefreshCw, Filter, Search, MapPin,
  TrendingUp, Brain, ChevronUp, ChevronDown, Zap, Target
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, PieChart, Pie, LineChart, Line,
  ComposedChart, Area, ReferenceLine
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { IncidentBoardPage } from '../incidents/IncidentBoardPage';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Incident {
  incident_id: string; officer_id: string; incident_type: string;
  region: string; city: string; description: string;
  incident_date: string; severity: number; response_time_minutes: number;
  assigned_precinct: string; status: string; time_to_complete: string;
  approved_by: string; location_detail: string; reported_by: string;
  victims_count: number; suspects_count: number;
  weapons_involved: boolean | string; property_damage: boolean | string;
  units_deployed: number; backup_requested: boolean | string;
}

const API_URL = (import.meta as any).env?.VITE_API_URL ||
  'https://nexus360-platform-backend-api-production.up.railway.app';
const PAL = ['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899','#84cc16','#f43f5e'];
const REGIONS = ['Adamaoua','Centre','Est','Extrême-Nord','Littoral','Nord','Nord-Ouest','Ouest','Sud','Sud-Ouest'];
const CITY_COORDS: Record<string,[number,number]> = {
  'Yaoundé':[3.8667,11.5167],'Douala':[4.0511,9.7679],'Garoua':[9.3,13.4],
  'Maroua':[10.5958,14.3249],'Bamenda':[5.9597,10.1459],'Bafoussam':[5.4767,10.4175],
  'Ngaoundéré':[7.3167,13.5833],'Bertoua':[4.5833,13.6833],'Ebolowa':[2.9,11.15],
  'Kumba':[4.6333,9.4333],'Limbé':[4.0167,9.2],'Edéa':[3.7956,10.1289],
  'Kribi':[2.9392,9.9086],'Nkongsamba':[4.9500,9.9333],'Foumban':[5.7167,10.9167],
  'Yagoua':[10.3427,15.2366],'Mokolo':[10.7500,13.8000],'Kousséri':[12.0817,15.0306],
  'Buea':[4.1597,9.2417],'Sangmélima':[2.9333,11.9833],'Dschang':[5.4333,10.0667],
  'Meiganga':[6.5167,14.3000],'Tibati':[6.4667,12.6333],'Guider':[9.9333,13.9500],
  'Wum':[6.3833,10.0667],'Nanga Eboko':[4.6833,12.3667],'Batouri':[4.4333,14.3667],
  'Abong-Mbang':[3.9833,13.1667],'Meyomessala':[3.0000,12.4000],'Akono':[3.5667,11.4167],
};
const CITY_COORDS_EXT: Record<string,[number,number]> = {
  ...CITY_COORDS,
  'Bafia':[4.7500,11.2333],'Mbalmayo':[3.5167,11.5000],'Obala':[4.1667,11.5333],
  'Tiko':[4.0781,9.3603],'Mamfe':[5.7667,9.2833],'Kumbo':[6.2000,10.6667],
  'Mbouda':[5.6333,10.2500],'Bafang':[5.1500,10.1833],'Loum':[4.7167,9.7333],
  'Poli':[8.4833,13.2333],'Ngong':[9.9500,13.5667],'Lagdo':[9.0500,13.6500],
  'Banyo':[6.7500,11.8167],'Kaele':[10.1000,14.4500],'Mora':[11.0500,14.1333],
  'Garoua-Boulai':[5.8833,14.5500],'Yokadouma':[3.5167,15.0500],
};
const INCIDENT_TYPES = [
  'Perturbation de l\'ordre public','Vol avec violence','Accident de circulation',
  'Violence domestique','Trafic de drogue','Cambriolage','Agression',
  'Manifestation','Fraude','Enlèvement'
];

const n = (v: any, fb = 0) => { const x = Number(v); return isNaN(x) ? fb : x; };
const bl = (v: any) => v === true || String(v).toUpperCase() === 'TRUE' || v === '1';
const isCritical = (inc: Incident) => n(inc.severity) === 1;
const isResolved = (inc: Incident) => ['Résolu','Clôturé'].includes(inc.status);
const isPending  = (inc: Incident) => ['Ouvert','En cours','En attente de validation'].includes(inc.status);

function computeRiskScore(incidents: Incident[]): number {
  if (!incidents.length) return 0;
  const critRate   = incidents.filter(isCritical).length / Math.max(incidents.length,1);
  const pendRate   = incidents.filter(isPending).length  / incidents.length;
  const avgSeverity= incidents.reduce((s,i)=>s+n(i.severity),0)/incidents.length;
  const avgResp    = incidents.reduce((s,i)=>s+n(i.response_time_minutes),0)/incidents.length;
  const raw = critRate*0.35+pendRate*0.25+(avgSeverity/5)*0.25+Math.min(avgResp/120,1)*0.15;
  return Math.round(100/(1+Math.exp(-8*(raw-0.4)))*10)/10;
}
function riskLabel(score: number): string { return score>=70?'CRITICAL':score>=40?'MODERATE':'LOW'; }
function recommendedDeployment(score:number,critRate:number,pendRate:number):string{
  if(score>=70&&critRate>=.3)return 'Déployer Unité d\'Intervention Rapide (50+ officiers) · Patrouille 24/7';
  if(score>=70)return 'Déployer Force d\'Intervention Mobile · Renforcer effectifs de 40%';
  if(score>=40&&pendRate>=.3)return 'Renforcer patrouille (20+ officiers) · Programme liaison communautaire';
  if(score>=40)return 'Renforcement standard patrouille · Surveillance préventive';
  return 'Patrouille de routine · Surveillance continue';
}
function predictHotspot(cityIncs:Incident[],_all:Incident[]):number{
  if(!cityIncs.length)return 0;
  const now=Date.now(),d7=7*24*3600*1000,d30=30*24*3600*1000;
  const recent=cityIncs.filter(i=>now-new Date(i.incident_date).getTime()<d30).length;
  const prior=cityIncs.filter(i=>{const a=now-new Date(i.incident_date).getTime();return a>=d30&&a<2*d30;}).length;
  const freqTrend=prior>0?Math.min((recent-prior)/prior,1):recent>0?.5:0;
  const backlogRate=cityIncs.filter(i=>isPending(i)).length/cityIncs.length;
  const thisMonth=new Date().getMonth();
  const seasonalRate=Math.min(cityIncs.filter(i=>new Date(i.incident_date).getMonth()===thisMonth).length/Math.max(cityIncs.length,1),1);
  const rec7=cityIncs.filter(i=>now-new Date(i.incident_date).getTime()<d7);
  const avgResp=rec7.length>0?rec7.reduce((s,i)=>s+n(i.response_time_minutes),0)/rec7.length:60;
  const respDeg=Math.min(avgResp/120,1);
  const raw=Math.max(0,freqTrend)*0.35+backlogRate*0.30+seasonalRate*0.20+respDeg*0.15;
  return Math.round(100/(1+Math.exp(-6*(raw-0.4)))*10)/10;
}

// ── WOW: KPI Card ─────────────────────────────────────────────────────────────
const KPICard=({icon:Icon,label,numValue,suffix='',color,bg,border,alert=false,decimals=0}:
  {icon:any;label:string;numValue:number;suffix?:string;color:string;bg:string;border:string;alert?:boolean;decimals?:number})=>{
  const glowColor=color.includes('red')?'rgba(239,68,68,0.2)':color.includes('green')?'rgba(16,185,129,0.2)':color.includes('orange')?'rgba(249,115,22,0.2)':color.includes('purple')?'rgba(168,85,247,0.2)':'rgba(59,130,246,0.2)';
  return(
    <motion.div whileHover={{scale:1.05,boxShadow:`0 0 20px ${glowColor}`}} transition={{duration:.15}}
      className={`rounded-xl p-3 border ${border} ${bg} flex items-center gap-3 cursor-default relative overflow-hidden`}
      style={{backdropFilter:'blur(8px)',background:'linear-gradient(135deg,rgba(15,23,42,0.85) 0%,rgba(30,41,59,0.6) 100%)'}}>
      <motion.div className="absolute top-0 left-0 right-0 h-px opacity-50"
        style={{background:`linear-gradient(90deg,transparent,${color.includes('red')?'#ef4444':color.includes('green')?'#10b981':color.includes('orange')?'#f97316':color.includes('purple')?'#a855f7':'#3b82f6'},transparent)`}}
        animate={{x:['-100%','100%']}} transition={{duration:2.5,repeat:Infinity,ease:'linear',repeatDelay:1}}/>
      <Icon className={`w-5 h-5 ${color} flex-shrink-0 ${alert?'animate-pulse':''}`}/>
      <div>
        <p className={`font-bold text-lg leading-none ${color}`}>
          <CountUp end={numValue} duration={1.5} decimals={decimals} suffix={suffix} preserveValue separator=" "/>
        </p>
        <p className="text-slate-400 text-xs mt-0.5">{label}</p>
      </div>
      {alert&&<motion.div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-400"
        animate={{opacity:[1,.2,1],scale:[1,1.5,1]}} transition={{duration:1,repeat:Infinity}}/>}
    </motion.div>
  );
};

// ── WOW: Skeleton ─────────────────────────────────────────────────────────────
const SkeletonCard=()=>(
  <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={14} width="55%" className="mb-3"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={28} width="35%" className="mb-2"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={10} count={2}/>
  </div>
);

// ── WOW: LIVE badge ───────────────────────────────────────────────────────────
const LiveBadge=({lastUpd,refreshing}:{lastUpd:Date;refreshing:boolean})=>(
  <div className="flex items-center gap-2">
    <motion.span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30"
      animate={{opacity:refreshing?[1,.5,1]:1}} transition={{duration:.6,repeat:refreshing?Infinity:0}}>
      <motion.span className="w-1.5 h-1.5 rounded-full bg-green-400"
        animate={{opacity:[1,.3,1],scale:[1,1.4,1]}} transition={{duration:1.5,repeat:Infinity}}/>
      LIVE
    </motion.span>
    <span className="text-slate-500 text-xs">{refreshing?'Actualisation…':`${lastUpd.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}`}</span>
  </div>
);

// ── WOW: Pulsing Alert Banner ─────────────────────────────────────────────────
const AlertBanner=({criticals,pending}:{criticals:number;pending:number})=>{
  if(criticals===0&&pending<30)return null;
  return(
    <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}}
      className="rounded-xl border border-red-500/40 overflow-hidden relative"
      style={{background:'linear-gradient(135deg,rgba(127,29,29,0.25) 0%,rgba(15,23,42,0.8) 100%)'}}>
      <motion.div className="absolute inset-0 rounded-xl pointer-events-none"
        animate={{boxShadow:['0 0 0px rgba(239,68,68,0)','0 0 24px rgba(239,68,68,0.18)','0 0 0px rgba(239,68,68,0)']}}
        transition={{duration:2,repeat:Infinity}}/>
      <div className="flex items-center gap-3 p-3 relative z-10">
        <motion.div animate={{rotate:[0,-12,12,-12,0]}} transition={{duration:.5,repeat:Infinity,repeatDelay:2.5}}>
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0"/>
        </motion.div>
        <div className="flex-1 flex flex-wrap gap-2 items-center">
          <span className="text-red-300 font-bold text-sm">Alertes actives</span>
          {criticals>0&&<motion.span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full border border-red-500/30"
            animate={{opacity:[1,.6,1]}} transition={{duration:1.5,repeat:Infinity}}>
            🔴 {criticals} incidents P1-Critique
          </motion.span>}
          {pending>=30&&<motion.span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full border border-orange-500/30"
            animate={{opacity:[1,.6,1]}} transition={{duration:1.5,repeat:Infinity,delay:.4}}>
            🟠 {pending} incidents en attente de traitement
          </motion.span>}
        </div>
      </div>
    </motion.div>
  );
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const NexTip = ({active,payload,label}:any)=>{
  if(!active||!payload?.length)return null;
  return(<motion.div initial={{opacity:0,scale:.95}} animate={{opacity:1,scale:1}} transition={{duration:.12}}
    className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs backdrop-blur min-w-36">
    <p className="text-white font-semibold mb-1.5 pb-1 border-b border-slate-700">{label}</p>
    {payload.map((p:any,i:number)=>(
      <div key={i} className="flex justify-between gap-3 py-0.5">
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{background:p.fill||p.stroke||p.color}}/><span className="text-slate-300">{p.name}</span></div>
        <span className="font-bold" style={{color:p.fill||p.stroke||p.color}}>{p.value}</span>
      </div>
    ))}
  </motion.div>);
};

// ── Card with glassmorphism ───────────────────────────────────────────────────
const Card=({icon:Icon,title,children,accent='text-blue-400',className=''}:
  {icon:any;title:string;children:React.ReactNode;accent?:string;className?:string})=>(
  <motion.div whileHover={{boxShadow:'0 0 28px rgba(59,130,246,0.07)'}}
    className={`rounded-xl border border-blue-900/40 overflow-hidden shadow-lg ${className}`}
    style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
      style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
      <Icon className={`w-4 h-4 ${accent}`}/><span className="text-sm font-semibold text-white">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </motion.div>
);

const PBar=({value,color='bg-blue-500'}:{value:number;color?:string})=>(
  <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
    <motion.div className={`h-1.5 rounded-full ${color}`}
      initial={{width:0}} animate={{width:`${Math.min(100,value)}%`}} transition={{duration:1,ease:'easeOut'}}/>
  </div>
);

// ── Risk Gauge with animated arc ──────────────────────────────────────────────
const RiskGauge=({score}:{score:number})=>{
  const color=score>=70?'#ef4444':score>=50?'#f97316':score>=30?'#eab308':'#10b981';
  const label=score>=70?'CRITIQUE':score>=50?'ÉLEVÉ':score>=30?'MODÉRÉ':'FAIBLE';
  const r=80,cx=100,cy=105;
  const toR=(d:number)=>d*Math.PI/180;
  const px=(d:number)=>cx+r*Math.cos(toR(d));
  const py=(d:number)=>cy-r*Math.sin(toR(d));
  const needleDeg=180-(score/100)*180;
  const nx=cx+(r-18)*Math.cos(toR(needleDeg));
  const ny=cy-(r-18)*Math.sin(toR(needleDeg));
  return(
    <div className="flex flex-col items-center">
      <svg width="200" height="210" viewBox="0 0 200 210">
        <defs><linearGradient id="riskGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#10b981"/><stop offset="30%" stopColor="#eab308"/>
          <stop offset="60%" stopColor="#f97316"/><stop offset="100%" stopColor="#ef4444"/>
        </linearGradient></defs>
        <path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(0)} ${py(0)}`} fill="none" stroke="#1e293b" strokeWidth="14" strokeLinecap="round"/>
        {score>0&&<motion.path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(needleDeg)} ${py(needleDeg)}`}
          fill="none" stroke="url(#riskGrad)" strokeWidth="14" strokeLinecap="round"
          initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:2,ease:'easeOut'}}/>}
        {[0,25,50,75,100].map(v=>{
          const deg=180-(v/100)*180;
          const x1=cx+(r-20)*Math.cos(toR(deg)),y1=cy-(r-20)*Math.sin(toR(deg));
          const x2=cx+(r-7)*Math.cos(toR(deg)),y2=cy-(r-7)*Math.sin(toR(deg));
          return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1.5"/>;
        })}
        <motion.line x1={cx} y1={cy} x2={cx} y2={cy-(r-18)}
          animate={{x2:nx,y2:ny}} transition={{type:'spring',stiffness:50,damping:15,delay:.6}}
          stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r="6" fill="white"/>
        <text x={cx} y={cy+58} textAnchor="middle" fill="white" fontSize="28" fontWeight="bold">{score.toFixed(1)}</text>
        <text x={cx} y={cy+86} textAnchor="middle" fill={color} fontSize="20" fontWeight="bold">{label}</text>
        <text x="14" y="118" fill="#10b981" fontSize="8">0</text>
        <text x="178" y="118" fill="#ef4444" fontSize="8">100</text>
      </svg>
    </div>
  );
}

// ── Map components ────────────────────────────────────────────────────────────
const MapResizer=()=>{const map=useMap();useEffect(()=>{setTimeout(()=>map.invalidateSize(),300);},[map]);return null;};
const MapFlyTo=({coords}:{coords:[number,number]|null})=>{const map=useMap();useEffect(()=>{if(coords)map.flyTo(coords,10,{animate:true,duration:1.2});else map.flyTo([5.5,12.3],6,{animate:true,duration:1.0});},[coords,map]);return null;};

const CommandMap=({incidents,onCitySelect,selectedCity}:{incidents:Incident[];onCitySelect:(c:string)=>void;selectedCity:string|null})=>{
  const[mode,setMode]=useState<'hist'|'pred'>('hist');
  const allCoords=useMemo(()=>({...CITY_COORDS_EXT}),[]);
  const cityStats=useMemo(()=>{
    const m:Record<string,{total:number;resolved:number;critical:number;pending:number;region:string;avgResp:number}>={};
    incidents.forEach(i=>{if(!m[i.city])m[i.city]={total:0,resolved:0,critical:0,pending:0,region:i.region,avgResp:0};m[i.city].total++;m[i.city].avgResp+=n(i.response_time_minutes);if(isResolved(i))m[i.city].resolved++;if(isCritical(i))m[i.city].critical++;if(isPending(i))m[i.city].pending++;});
    Object.values(m).forEach(s=>{if(s.total>0)s.avgResp=Math.round(s.avgResp/s.total);});
    return m;
  },[incidents]);
  const hotspots=useMemo(()=>{
    const m:Record<string,Incident[]>={};incidents.forEach(i=>{if(!m[i.city])m[i.city]=[];m[i.city].push(i);});
    return Object.entries(m).map(([city,incs])=>({city,incs,score:predictHotspot(incs,incidents)})).filter(d=>(allCoords as any)[d.city]&&d.score>10).sort((a,b)=>b.score-a.score);
  },[incidents,allCoords]);
  const maxTotal=Math.max(...Object.values(cityStats).map(s=>s.total),1);
  const selCoords=selectedCity?(allCoords as any)[selectedCity]||null:null;
  const histColor=(s:{critical:number;total:number})=>{const r=s.critical/s.total;return r>=.3?'#ef4444':r>=.15?'#f97316':r>=.05?'#eab308':'#10b981';};
  const predColor=(sc:number)=>sc>=70?'#ef4444':sc>=50?'#f97316':sc>=30?'#eab308':'#3b82f6';
  return(
    <div className="space-y-3">
      <style>{`.inc-tip .leaflet-tooltip{background:transparent!important;border:none!important;box-shadow:none!important;padding:0!important}.inc-tip .leaflet-tooltip::before{display:none!important}.leaflet-container{background:#0f172a!important}.leaflet-control-zoom a{background:#1e293b!important;color:#94a3b8!important;border-color:#334155!important}`}</style>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 bg-slate-700/60 rounded-lg p-1">
          {([['hist','Analyse temps réel','📊'],['pred','Prédiction hotspots 30j','🔮']] as const).map(([k,label,icon])=>(
            <button key={k} onClick={()=>setMode(k)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode===k?'bg-blue-600 text-white shadow-lg':'text-slate-400 hover:text-white'}`}>
              {icon} {label}
            </button>
          ))}
        </div>
        {selectedCity&&<button onClick={()=>onCitySelect(selectedCity)} className="text-xs text-blue-300 hover:text-white border border-blue-500/30 bg-blue-500/10 px-2.5 py-1 rounded-lg">📍 {selectedCity} ✕</button>}
      </div>
      <div className="relative rounded-xl overflow-hidden border border-blue-900/40" style={{height:'460px'}}>
        <MapContainer center={[5.5,12.3]} zoom={6} style={{width:'100%',height:'100%',background:'#0f172a'}} attributionControl={false} scrollWheelZoom>
          <MapFlyTo coords={selCoords}/>
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" subdomains="abcd" maxZoom={19}/>
          {mode==='hist'&&Object.entries(cityStats).map(([city,s])=>{
            const coords=(allCoords as any)[city];if(!coords)return null;
            const color=histColor(s),isSel=selectedCity===city;
            const radius=Math.max(7,Math.min(26,7+(s.total/maxTotal)*20));
            return(<CircleMarker key={city} center={coords} radius={isSel?radius+5:radius}
              pathOptions={{color:isSel?'#fff':color,weight:isSel?3:1.5,fillColor:color,fillOpacity:isSel?.95:.6}}
              eventHandlers={{click:()=>onCitySelect(city)}}>
              <LeafletTooltip className="inc-tip" direction="top" offset={[0,-radius]} opacity={1}>
                <div style={{background:'#0a0f1e',border:'1.5px solid '+color,borderRadius:'14px',padding:'14px',minWidth:'200px',fontFamily:'system-ui,sans-serif',color:'white',boxShadow:'0 8px 32px '+color+'66'}}>
                  <div style={{fontWeight:700,color,marginBottom:'8px',fontSize:'12px'}}>📍 {city} — {s.region}</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                    {[['📊',s.total,'Incidents','#3b82f6'],['✅',Math.round(s.resolved/s.total*100)+'%','Resolution','#10b981'],['⚠️',Math.round(s.critical/s.total*100)+'%','Critiques','#ef4444'],['📋',s.pending,'En attente','#f97316']].map(([ic,val,lbl,col])=>(
                      <div key={String(lbl)} style={{background:'#1e293b',borderRadius:'8px',padding:'6px',textAlign:'center'}}>
                        <div style={{fontSize:'18px',fontWeight:'bold',color:String(col)}}>{val}</div>
                        <div style={{color:'#64748b',fontSize:'9px'}}>{ic} {lbl}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:'8px',fontSize:'10px',color:'#64748b',borderTop:'1px solid #1e293b',paddingTop:'7px',display:'flex',justifyContent:'space-between'}}>
                    <span>Delai moy: <span style={{color:'#a855f7',fontWeight:'bold'}}>{s.avgResp}mn</span></span>
                    <span style={{color:'#3b82f6'}}>Cliquer pour filtrer</span>
                  </div>
                </div>
              </LeafletTooltip>
            </CircleMarker>);
          })}
          {mode==='pred'&&hotspots.map(({city,score,incs})=>{
            const coords=(allCoords as any)[city];if(!coords)return null;
            const color=predColor(score),isSel=selectedCity===city;
            const radius=Math.max(10,Math.min(34,10+(score/100)*24));
            const critRate=Math.round(incs.filter(isCritical).length/incs.length*100);
            return(<CircleMarker key={city} center={coords} radius={isSel?radius+5:radius}
              pathOptions={{color:isSel?'#fff':color,weight:isSel?3:2,fillColor:color,fillOpacity:.5}}
              eventHandlers={{click:()=>onCitySelect(city)}}>
              <LeafletTooltip className="inc-tip" direction="top" offset={[0,-radius]} opacity={1}>
                <div style={{background:'#0a0f1e',border:'1.5px solid '+color,borderRadius:'14px',padding:'14px',minWidth:'210px',fontFamily:'system-ui,sans-serif',color:'white',boxShadow:'0 8px 32px '+color+'77'}}>
                  <div style={{fontWeight:700,color,marginBottom:'6px',fontSize:'11px'}}>🔮 {city} — PREDICTION 30J</div>
                  <div style={{fontSize:'22px',fontWeight:'bold',color,textAlign:'center',marginBottom:'8px'}}>{score.toFixed(1)}% <span style={{fontSize:'11px',color:'#94a3b8',fontWeight:'normal'}}>prob. incident</span></div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                    {[['Hist.',incs.length,'#3b82f6'],['Critiques',critRate+'%','#ef4444'],['Backlog',incs.filter(isPending).length,'#f97316'],['Delai moy.',Math.round(incs.reduce((s,i)=>s+n(i.response_time_minutes),0)/incs.length)+'mn','#a855f7']].map(([lbl,val,col])=>(
                      <div key={String(lbl)} style={{background:'#1e293b',borderRadius:'8px',padding:'5px',textAlign:'center'}}>
                        <div style={{fontSize:'15px',fontWeight:'bold',color:String(col)}}>{val}</div>
                        <div style={{color:'#64748b',fontSize:'9px'}}>{lbl}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </LeafletTooltip>
            </CircleMarker>);
          })}
        </MapContainer>
        <div className="absolute bottom-10 left-3 bg-slate-900/92 border border-blue-900/40 rounded-lg p-2.5 pointer-events-none" style={{zIndex:1000}}>
          {mode==='hist'
            ?[['#10b981','< 5%'],['#eab308','5-15%'],['#f97316','15-30%'],['#ef4444','>= 30%']].map(([c,l])=><div key={l} className="flex items-center gap-1.5 mb-0.5"><span className="w-2 h-2 rounded-full" style={{background:c}}/><span className="text-slate-300" style={{fontSize:'9px'}}>{l}</span></div>)
            :[['#ef4444','>= 70% Critique'],['#f97316','50-70% Eleve'],['#eab308','30-50% Modere'],['#3b82f6','< 30% Faible']].map(([c,l])=><div key={l} className="flex items-center gap-1.5 mb-0.5"><span className="w-2 h-2 rounded-full" style={{background:c}}/><span className="text-slate-300" style={{fontSize:'9px'}}>{l}</span></div>)}
        </div>
        {mode==='pred'&&<div className="absolute top-3 right-3 bg-slate-900/92 border border-blue-900/40 rounded-lg p-2.5 pointer-events-none" style={{zIndex:1000}}>
          <p className="text-white text-xs font-bold mb-2">Top hotspots prédits</p>
          {hotspots.slice(0,5).map((d,i)=>{const rc=predColor(d.score);return(<div key={d.city} className="flex items-center justify-between gap-3 mb-1"><span className="text-slate-300 text-xs">{i+1}. {d.city}</span><span className="text-xs font-bold" style={{color:rc}}>{d.score.toFixed(0)}%</span></div>);})}
        </div>}
      </div>
    </div>
  );
};

// ── SEV styles + LiveTicker ───────────────────────────────────────────────────
const SEV_STYLE:Record<number,{bg:string;text:string;label:string}>={
  1:{bg:'#ef4444',text:'white',label:'P1 - CRITIQUE'},
  2:{bg:'#f97316',text:'white',label:'P2 - ÉLEVÉ'},
  3:{bg:'#eab308',text:'black',label:'P3 - MODÉRÉ'},
  4:{bg:'#3b82f6',text:'white',label:'P4 - FAIBLE'},
  5:{bg:'#10b981',text:'white',label:'P5 - MINEUR'},
};
const LiveTicker=({incidents}:{incidents:Incident[]})=>{
  const[idx,setIdx]=React.useState(0);
  const sorted=React.useMemo(()=>[...incidents].sort((a,b)=>new Date(b.incident_date).getTime()-new Date(a.incident_date).getTime()).slice(0,20),[incidents]);
  React.useEffect(()=>{if(!sorted.length)return;const t=setInterval(()=>setIdx(i=>(i+1)%sorted.length),4000);return()=>clearInterval(t);},[sorted.length]);
  if(!sorted.length)return null;
  const inc=sorted[idx];
  const bg=(SEV_STYLE[n(inc.severity)]||SEV_STYLE[3]).bg||'#3b82f6';
  const label=(SEV_STYLE[n(inc.severity)]||SEV_STYLE[3]).label||'MODÉRÉ';
  const ageH=Math.round((Date.now()-new Date(inc.incident_date).getTime())/3600000);
  const text=`${label}  —  ${inc.city}  ·  ${inc.incident_type}  ·  ${inc.region}  ·  Sev. ${inc.severity}/5  ·  ${ageH<24?ageH+'h ago':Math.round(ageH/24)+'j ago'}`;
  return(
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-blue-900/40 overflow-hidden relative"
      style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.6) 100%)'}}>
      <style>{`@keyframes sweep{0%{transform:translateX(100%)}100%{transform:translateX(-100%)}}.sweep-text{display:inline-block;white-space:nowrap;animation:sweep 12s linear infinite;}`}</style>
      <motion.span className="w-2 h-2 rounded-full flex-shrink-0 z-10"
        animate={{opacity:[1,.3,1],scale:[1,1.5,1]}} transition={{duration:1,repeat:Infinity}}
        style={{background:bg}}/>
      <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 text-white z-10" style={{background:bg}}>{label}</span>
      <motion.span className="text-white text-xs font-bold flex-shrink-0 z-10 bg-green-500/20 border border-green-500/30 px-2 py-0.5 rounded-full"
        animate={{opacity:[1,.6,1]}} transition={{duration:1.5,repeat:Infinity}}>LIVE</motion.span>
      <div className="flex-1 overflow-hidden relative">
        <motion.div key={idx} className="sweep-text text-xs text-slate-200 font-medium">{text}<span className="mx-8 text-slate-500">|</span>{text}</motion.div>
      </div>
      <div className="flex gap-0.5 flex-shrink-0 z-10">
        {sorted.map((_,i)=><span key={i} className={"h-1.5 rounded-full transition-all "+(i===idx?'w-3 bg-blue-400':'w-1.5 bg-slate-600')}/>)}
      </div>
    </div>
  );
};

// ── Velocity Chart ────────────────────────────────────────────────────────────
const VelocityChart=({incidents}:{incidents:Incident[]})=>{
  const now=Date.now(),d7=7*24*3600*1000;
  const data=REGIONS.map(r=>{
    const ri=incidents.filter(i=>i.region===r);
    const last7=ri.filter(i=>now-new Date(i.incident_date).getTime()<d7).length;
    const prev7=ri.filter(i=>{const a=now-new Date(i.incident_date).getTime();return a>=d7&&a<2*d7;}).length;
    const delta=prev7>0?Math.round(((last7-prev7)/prev7)*100):last7>0?100:0;
    return{region:r.replace('Extreme-Nord','Ex-Nord').replace('Extrême-Nord','Ex-Nord').replace('Nord-Ouest','N-Ouest').replace('Sud-Ouest','S-Ouest'),last7,prev7,delta};
  }).filter(d=>d.last7>0||d.prev7>0).sort((a,b)=>b.delta-a.delta);
  const maxL=Math.max(...data.map(d=>d.last7),1);
  return(
    <div className="space-y-2">
      {data.map((d,i)=>(
        <motion.div key={d.region} initial={{opacity:0,x:12}} animate={{opacity:1,x:0}} transition={{delay:i*.05}} className="flex items-center gap-3">
          <span className="text-slate-300 text-xs w-20 flex-shrink-0 text-right">{d.region}</span>
          <div className="flex-1 relative h-5">
            <div className="absolute inset-0 bg-slate-700/40 rounded-full overflow-hidden">
              <motion.div className="h-full rounded-full" initial={{width:0}}
                animate={{width:`${Math.min(100,Math.max(5,(d.last7/maxL)*100))}%`}} transition={{duration:.8,delay:i*.05}}
                style={{background:d.delta>15?'#ef4444':d.delta>5?'#f97316':d.delta<-10?'#10b981':'#3b82f6'}}/>
            </div>
            <span className="absolute right-2 top-0 bottom-0 flex items-center text-xs text-white font-medium">{d.last7}</span>
          </div>
          <div className={`flex items-center gap-0.5 text-xs font-bold w-16 flex-shrink-0 ${d.delta>10?'text-red-400':d.delta<-10?'text-green-400':'text-slate-400'}`}>
            <span>{d.delta>=0?'↑':'↓'}</span><span>{Math.abs(d.delta)}%</span>
            {d.delta>15&&<motion.span className="ml-0.5 text-red-400" animate={{opacity:[1,.3,1]}} transition={{duration:.8,repeat:Infinity}}>!</motion.span>}
          </div>
          <div className="text-slate-500 text-xs w-8 flex-shrink-0">{d.prev7}p</div>
        </motion.div>
      ))}
      <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-blue-900/30 mt-2">
        <span>7 derniers jours vs 7 jours précédents</span>
        <span className="flex items-center gap-2"><span className="text-red-400">↑ Hausse</span><span className="text-green-400">↓ Baisse</span></span>
      </div>
    </div>
  );
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export const IncidentsAnalyticsPageInner=({user}:{user:any})=>{
  const[incidents,setIncidents]=useState<Incident[]>([]);
  const[loading,setLoading]=useState(true);
  const[refreshing,setRefreshing]=useState(false);
  const[lastUpd,setLastUpd]=useState(new Date());
  const[dateFrom,setDateFrom]=useState('');
  const[dateTo,setDateTo]=useState('');
  const[regionF,setRegionF]=useState('');
  const[typeF,setTypeF]=useState('');
  const[severityF,setSeverityF]=useState('');
  const[search,setSearch]=useState('');
  const[selectedCity,setSelectedCity]=useState<string|null>(null);
  const[riskSortAsc,setRiskSortAsc]=useState(false);
  const[riskPage,setRiskPage]=useState(0);
  const RISK_PAGE=10;

  const fetchIncidents=useCallback(async()=>{
    setRefreshing(true);
    try{
      const res=await fetch(`${API_URL}/api/incidents`);
      const json=await res.json();
      if(json.success&&Array.isArray(json.data)){
        setIncidents(json.data.map((i:any)=>({...i,severity:n(i.severity),response_time_minutes:n(i.response_time_minutes),victims_count:n(i.victims_count),suspects_count:n(i.suspects_count),units_deployed:n(i.units_deployed)})));
      }
    }catch(e){console.error('Incidents fetch:',e);}
    finally{setLoading(false);setRefreshing(false);setLastUpd(new Date());}
  },[]);
  useEffect(()=>{fetchIncidents();},[fetchIncidents]);
  useEffect(()=>{const t=setInterval(fetchIncidents,30000);return()=>clearInterval(t);},[fetchIncidents]);

  const filtered=useMemo(()=>incidents.filter(inc=>{
    const d=String(inc.incident_date).slice(0,10);
    if(dateFrom&&d<dateFrom)return false;
    if(dateTo&&d>dateTo)return false;
    if(regionF&&inc.region!==regionF)return false;
    if(typeF&&inc.incident_type!==typeF)return false;
    if(selectedCity&&inc.city!==selectedCity)return false;
    if(severityF&&n(inc.severity)!==Number(severityF))return false;
    if(search&&![inc.city,inc.region,inc.incident_type,inc.incident_id].join(' ').toLowerCase().includes(search.toLowerCase()))return false;
    return true;
  }),[incidents,dateFrom,dateTo,regionF,typeF,selectedCity,severityF,search]);

  const total=filtered.length;
  const criticals=filtered.filter(isCritical).length;
  const resolved=filtered.filter(isResolved).length;
  const pending=filtered.filter(isPending).length;
  const critRate=total>0?Math.round(criticals/total*100*10)/10:0;
  const resRate=total>0?Math.round(resolved/total*100*10)/10:0;
  const avgResp=total>0?Math.round(filtered.reduce((s,i)=>s+n(i.response_time_minutes),0)/total):0;
  const globalRisk=computeRiskScore(filtered);

  const donutData=useMemo(()=>{
    const m:Record<string,number>={};filtered.forEach(i=>{m[i.incident_type]=(m[i.incident_type]||0)+1;});
    return Object.entries(m).map(([name,value],i)=>({name,value,fill:PAL[i%PAL.length]})).sort((a,b)=>b.value-a.value).slice(0,7);
  },[filtered]);
  const top5Regions=useMemo(()=>REGIONS.map(r=>({region:r.replace('Extrême-Nord','Ex-Nord').replace('Nord-Ouest','N-Ouest').replace('Sud-Ouest','S-Ouest'),total:filtered.filter(i=>i.region===r).length,critical:filtered.filter(i=>i.region===r&&isCritical(i)).length})).sort((a,b)=>b.total-a.total).slice(0,5),[filtered]);
  const trendData=useMemo(()=>{
    const m:Record<string,{total:number;critical:number;resolved:number}>={};
    filtered.forEach(i=>{const ym=String(i.incident_date).slice(0,7);if(!m[ym])m[ym]={total:0,critical:0,resolved:0};m[ym].total++;if(isCritical(i))m[ym].critical++;if(isResolved(i))m[ym].resolved++;});
    return Object.entries(m).sort().slice(-15).map(([month,d])=>({month,total:d.total,critical:d.critical,resRate:d.total>0?Math.round(d.resolved/d.total*100):0}));
  },[filtered]);
  const cityRiskData=useMemo(()=>{
    const m:Record<string,Incident[]>={};filtered.forEach(i=>{if(!m[i.city])m[i.city]=[];m[i.city].push(i);});
    return Object.entries(m).map(([city,incs])=>{
      const score=computeRiskScore(incs),critR=incs.filter(isCritical).length/incs.length,pendR=incs.filter(isPending).length/incs.length;
      return{city,region:incs[0].region,total:incs.length,score,label:riskLabel(score),critRate:Math.round(critR*100),pendRate:Math.round(pendR*100),resRate:Math.round(incs.filter(isResolved).length/incs.length*100),avgResp:Math.round(incs.reduce((s,i)=>s+n(i.response_time_minutes),0)/incs.length),deployment:recommendedDeployment(score,critR,pendR)};
    }).sort((a,b)=>riskSortAsc?a.score-b.score:b.score-a.score);
  },[filtered,riskSortAsc]);
  const riskPaginated=cityRiskData.slice(riskPage*RISK_PAGE,(riskPage+1)*RISK_PAGE);
  const riskTotalPages=Math.ceil(cityRiskData.length/RISK_PAGE);
  const RISK_BG:Record<string,string>={CRITICAL:'text-red-400 bg-red-500/15 border-red-500/40',MODERATE:'text-orange-400 bg-orange-500/15 border-orange-500/40',LOW:'text-green-400 bg-green-500/15 border-green-500/40'};
  const RISK_COLOR:Record<string,string>={CRITICAL:'#ef4444',MODERATE:'#f97316',LOW:'#10b981'};
  const fadeUp={initial:{opacity:0,y:20},animate:{opacity:1,y:0},transition:{duration:.5}};

  // ── Skeleton loader ──
  if(loading)return(
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <Skeleton baseColor="#1e293b" highlightColor="#334155" width={300} height={28}/>
        <Skeleton baseColor="#1e293b" highlightColor="#334155" width={60} height={22} borderRadius={20}/>
      </div>
      <Skeleton baseColor="#1e293b" highlightColor="#334155" height={44} borderRadius={12}/>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Array(5).fill(0).map((_,i)=><SkeletonCard key={i}/>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SkeletonCard/><SkeletonCard/><SkeletonCard/>
      </div>
      <div className="text-center text-slate-500 text-sm flex items-center justify-center gap-2 pt-4">
        <RefreshCw className="w-4 h-4 animate-spin text-blue-400"/>Chargement des incidents…
      </div>
    </div>
  );

  return(
    <div className="p-4 md:p-6 space-y-4" style={{background:'linear-gradient(180deg,rgba(2,6,23,0.25) 0%,transparent 100%)'}}>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <motion.div animate={{rotate:[0,-5,5,-5,0]}} transition={{duration:.5,repeat:Infinity,repeatDelay:4}}>
              <AlertTriangle className="w-6 h-6 text-red-400"/>
            </motion.div>
            <h1 className="text-2xl font-bold text-white">Intelligence Opérationnelle — Incidents</h1>
          </div>
          <p className="text-slate-400 text-sm">Analyse · ML Risque · {total} incidents</p>
        </div>
        <div className="flex items-center gap-3">
          <LiveBadge lastUpd={lastUpd} refreshing={refreshing}/>
          <button onClick={fetchIncidents} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 text-xs transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing?'animate-spin':''}`}/>Actualiser
          </button>
        </div>
      </div>

      {/* Pulsing Alert */}
      <AlertBanner criticals={criticals} pending={pending}/>

      {/* Live Ticker */}
      <AnimatePresence mode="wait"><LiveTicker incidents={incidents}/></AnimatePresence>

      {/* Filters */}
      <motion.div {...fadeUp} className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,0.8) 0%,rgba(30,41,59,0.5) 100%)',backdropFilter:'blur(8px)'}}>
        <Filter className="w-4 h-4 text-blue-400 flex-shrink-0"/>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-400 text-xs">Du</span>
          <input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setRiskPage(0);}} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white px-2 py-1 focus:outline-none focus:border-blue-500/60"/>
          <span className="text-slate-400 text-xs">Au</span>
          <input type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setRiskPage(0);}} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white px-2 py-1 focus:outline-none focus:border-blue-500/60"/>
        </div>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Ville / type…" className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-32 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{value:regionF,set:setRegionF,opts:REGIONS,ph:'Région'},{value:typeF,set:setTypeF,opts:INCIDENT_TYPES,ph:'Type'},{value:severityF,set:setSeverityF,opts:['1','2','3','4','5'],ph:'Sévérité'}].map((f,i)=>(
          <select key={i} value={f.value} onChange={e=>{f.set(e.target.value);setRiskPage(0);}} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none focus:border-blue-500/60">
            <option value="">{f.ph}</option>{f.opts.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        {(dateFrom||dateTo||regionF||typeF||severityF||search||selectedCity)&&(
          <button onClick={()=>{setDateFrom('');setDateTo('');setRegionF('');setTypeF('');setSeverityF('');setSearch('');setSelectedCity(null);setRiskPage(0);}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕ Effacer</button>
        )}
      </motion.div>

      {/* KPI Cards with CountUp */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.1}} className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPICard icon={AlertTriangle} label="Total incidents"           numValue={total}    color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
        <KPICard icon={Zap}           label="Taux incidents critiques"  numValue={critRate} suffix="%" decimals={1} color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert={critRate>20}/>
        <KPICard icon={CheckCircle}   label="Taux de résolution"        numValue={resRate}  suffix="%" decimals={1} color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KPICard icon={Clock}         label="En retard de traitement"   numValue={pending}                          color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30" alert={pending>30}/>
        <KPICard icon={Activity}      label="Délai moyen réponse"       numValue={avgResp}  suffix=" mn"            color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30"/>
      </motion.div>

      {/* Section header */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.12}}>
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-cyan-400"/>
          <span className="text-sm font-bold text-white">Analyse Descriptive</span>
          <div className="flex-1 h-px bg-blue-900/40"/>
        </div>
      </motion.div>

      {/* ROW 1: Gauge + Donut + Top5 */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.2}} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card icon={Shield} title="Score Global de Risque" accent="text-red-400">
          <RiskGauge score={globalRisk}/>
          <div className="grid grid-cols-3 gap-2 mt-16">
            {[
              {v:criticals,l:'Critiques',c:'text-red-400',bg:'bg-red-500/10',b:'border-red-500/30'},
              {v:pending,  l:'En attente',c:'text-orange-400',bg:'bg-orange-500/10',b:'border-orange-500/30'},
              {v:resolved, l:'Résolus',  c:'text-green-400',bg:'bg-green-500/10',b:'border-green-500/30'},
            ].map((s,i)=>(
              <motion.div key={i} whileHover={{scale:1.06}} className={`rounded-lg ${s.bg} border ${s.b} p-2 text-center`}>
                <p className={`${s.c} font-bold text-lg`}>
                  <CountUp end={s.v} duration={1.2} preserveValue/>
                </p>
                <p className="text-slate-400 text-xs">{s.l}</p>
              </motion.div>
            ))}
          </div>
        </Card>

        <Card icon={Activity} title="Répartition des incidents (%)" accent="text-blue-400">
          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="52%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={2} isAnimationActive animationDuration={1200} label={({percent})=>`${(percent*100).toFixed(0)}%`} labelLine={{stroke:'#94a3b8',strokeWidth:.8}}>
                {donutData.map((d,i)=><Cell key={i} fill={d.fill}/>)}
              </Pie>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{fontSize:'9px',paddingTop:'4px'}} formatter={(v:string)=>v.slice(0,22)}/>
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card icon={MapPin} title="Top 5 Régions en incidents" accent="text-orange-400">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={top5Regions} layout="vertical" margin={{top:4,right:40,left:4,bottom:4}} barCategoryGap="16%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
              <XAxis type="number" tick={{fill:'#94a3b8',fontSize:9}} allowDecimals={false}/>
              <YAxis type="category" dataKey="region" tick={{fill:'#cbd5e1',fontSize:10}} width={60}/>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{fontSize:'9px',paddingTop:'4px'}}/>
              <Bar dataKey="total"    name="Total"     fill="#3b82f6" stackId="a" radius={[0,0,0,0]} isAnimationActive animationDuration={800}/>
              <Bar dataKey="critical" name="Critiques" fill="#ef4444" stackId="a" radius={[0,3,3,0]} isAnimationActive animationDuration={900}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </motion.div>

      {/* Trend */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.25}}>
        <Card icon={TrendingUp} title="Tendances des incidents — Total / Critiques / Taux de résolution" accent="text-cyan-400">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={trendData} margin={{top:12,right:40,left:4,bottom:4}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f"/>
              <XAxis dataKey="month" tick={{fill:'#94a3b8',fontSize:9}} angle={0} textAnchor="middle" height={24} interval={0}/>
              <YAxis yAxisId="left" tick={{fill:'#94a3b8',fontSize:9}} allowDecimals={false} label={{value:'Nb incidents',angle:-90,position:'insideLeft',fill:'#64748b',fontSize:9,dx:8}}/>
              <YAxis yAxisId="right" orientation="right" domain={[0,100]} tick={{fill:'#94a3b8',fontSize:9}} tickFormatter={v=>`${v}%`} label={{value:'Taux résolution (%)',angle:90,position:'insideRight',fill:'#64748b',fontSize:9,dx:-8}}/>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{fontSize:'10px'}} verticalAlign="top" align="right"/>
              <Area yAxisId="left" type="monotone" dataKey="total" name="Total incidents" stroke="#3b82f6" fill="#3b82f644" strokeWidth={2} dot={{r:3}} isAnimationActive animationDuration={1500}/>
              <Bar yAxisId="left" dataKey="critical" name="Critiques" fill="#ef444488" radius={[2,2,0,0]} isAnimationActive animationDuration={1000}/>
              <Line yAxisId="right" type="monotone" dataKey="resRate" name="Taux résolution (%)" stroke="#10b981" strokeWidth={2.5} dot={{r:3}} isAnimationActive animationDuration={2000}/>
              <ReferenceLine yAxisId="right" y={80} stroke="#10b981" strokeDasharray="3 2" label={{value:'Cible 80%',fill:'#10b981',fontSize:8}}/>
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </motion.div>

      {/* CommandMap + Velocity */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.28}} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card icon={MapPin} title="Centre de Commandement — Analyse temps réel & Hotspots prédictifs" accent="text-green-400" className="h-full">
            <CommandMap incidents={filtered} onCitySelect={c=>setSelectedCity(c===selectedCity?null:c)} selectedCity={selectedCity}/>
            {selectedCity&&(
              <div className="mt-2 flex items-center justify-between">
                <span className="text-blue-300 text-xs font-medium">Filtre actif: {selectedCity} · {filtered.length} incidents</span>
                <button onClick={()=>setSelectedCity(null)} className="text-slate-400 text-xs hover:text-white">✕ Retirer filtre</button>
              </div>
            )}
          </Card>
        </div>
        <div>
          <Card icon={TrendingUp} title="Vélocité des incidents par région" accent="text-orange-400" className="h-full">
            <VelocityChart incidents={filtered}/>
          </Card>
        </div>
      </motion.div>

      {/* ML section header */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.3}}>
        <div className="flex items-center gap-2 mb-3 mt-2">
          <Brain className="w-4 h-4 text-purple-400"/>
          <span className="text-sm font-bold text-white">Analyses Prédictives — ML Risque</span>
          <div className="flex-1 h-px bg-blue-900/40"/>
          <motion.span className="text-xs text-purple-300 bg-purple-500/10 border border-purple-500/30 px-2 py-0.5 rounded-full"
            animate={{opacity:[1,.5,1]}} transition={{duration:2,repeat:Infinity}}>ML Actif</motion.span>
        </div>
      </motion.div>

      {/* ML Risk Table */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.35}}>
        <motion.div whileHover={{boxShadow:'0 0 28px rgba(59,130,246,0.07)'}}
          className="rounded-xl border border-blue-900/40 overflow-hidden shadow-lg"
          style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40 flex-wrap"
            style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
            <Brain className="w-4 h-4 text-purple-400 flex-shrink-0"/>
            <span className="text-sm font-semibold text-white">Tableau de Risques et Déploiement Recommandé</span>
            <span className="ml-auto text-xs text-slate-400">{cityRiskData.length} villes analysées</span>
            <button onClick={()=>setRiskSortAsc(s=>!s)} className="flex items-center gap-1 text-slate-400 text-xs hover:text-white ml-2">
              Score ML {riskSortAsc?<ChevronUp className="w-3 h-3"/>:<ChevronDown className="w-3 h-3"/>}
            </button>
          </div>
          <div className="overflow-x-auto">
            <div className="overflow-y-auto" style={{maxHeight:'480px'}}>
              <table className="w-full text-xs min-w-max">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-blue-900/60 bg-slate-700">
                    {['#','Ville','Région','Total','Critiques (%)','Résolus (%)','En attente (%)','Délai moy. (mn)','Score ML','Catégorie','Déploiement Recommandé'].map(h=>(
                      <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {riskPaginated.map((row,i)=>{
                      const rb=RISK_BG[row.label]||'',rc=RISK_COLOR[row.label]||'#10b981';
                      return(
                        <motion.tr key={row.city} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.02}}
                          whileHover={{backgroundColor:'rgba(59,130,246,0.05)'}}
                          className={`border-b border-blue-900/30 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                          <td className="px-3 py-2 text-center"><span className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-xs mx-auto">{riskPage*RISK_PAGE+i+1}</span></td>
                          <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{row.city}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{row.region}</td>
                          <td className="px-3 py-2 text-blue-300 text-center font-bold">{row.total}</td>
                          <td className="px-3 py-2 text-right"><span className={row.critRate>=30?'text-red-400 font-bold':row.critRate>=15?'text-orange-400':'text-slate-300'}>{row.critRate}%</span></td>
                          <td className="px-3 py-2 whitespace-nowrap min-w-24">
                            <div className="flex items-center gap-1.5">
                              <PBar value={row.resRate} color={row.resRate>=80?'bg-green-500':row.resRate>=60?'bg-blue-500':row.resRate>=40?'bg-yellow-500':'bg-red-500'}/>
                              <span className={row.resRate>=80?'text-green-400':row.resRate>=60?'text-blue-400':'text-yellow-400'}>{row.resRate}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-orange-400 text-right">{row.pendRate}%</td>
                          <td className="px-3 py-2 text-slate-300 text-center">{row.avgResp}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center gap-1.5 justify-end">
                              <div className="w-12 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                <motion.div className="h-full rounded-full" initial={{width:0}} animate={{width:`${row.score}%`}} transition={{duration:1,delay:i*.03}} style={{background:rc}}/>
                              </div>
                              <span className="font-bold" style={{color:rc}}>{row.score.toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${rb}`}
                              animate={row.label==='CRITICAL'?{opacity:[1,.7,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                              {row.label}
                            </motion.span>
                          </td>
                          <td className="px-3 py-2 text-slate-300 max-w-64" style={{minWidth:'200px'}}>
                            <span className="text-xs leading-relaxed">{row.deployment}</span>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                  {riskPaginated.length===0&&<tr><td colSpan={11} className="px-3 py-8 text-center text-slate-500">Aucune donnée</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="px-4 py-2.5 border-t border-blue-900/40 flex items-center justify-between text-xs text-slate-400">
            <span>{cityRiskData.length} ville(s) · Page {riskPage+1}/{Math.max(1,riskTotalPages)}</span>
            <div className="flex gap-2">
              <button onClick={()=>setRiskPage(p=>Math.max(0,p-1))} disabled={riskPage===0} className="px-2 py-1 rounded border border-slate-600/40 hover:bg-slate-700 disabled:opacity-30">&larr;</button>
              <button onClick={()=>setRiskPage(p=>Math.min(riskTotalPages-1,p+1))} disabled={riskPage>=riskTotalPages-1} className="px-2 py-1 rounded border border-slate-600/40 hover:bg-slate-700 disabled:opacity-30">&rarr;</button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Footer */}
      <div className="rounded-xl border border-blue-900/30 bg-slate-800/40 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-slate-400 text-xs italic">Intelligence Opérationnelle · ML risque prédictif · Actualisation 30s</p>
        <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-400"/><span className="text-blue-300 text-xs font-medium">Données souveraines · DGSN Cameroun</span></div>
      </div>
    </div>
  );
};

export const IncidentsAnalyticsPage = ({ user }: { user: any }) => {
  const role = (user?.role || '').toUpperCase();
  const isOfficier = role === 'OFFICIER';
  const [activeTab, setActiveTab] = React.useState<'intelligence'|'gestion'>(isOfficier ? 'gestion' : 'intelligence');
  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-1 rounded-xl p-1 flex-wrap"
        style={{background:'linear-gradient(135deg,rgba(30,41,59,.8) 0%,rgba(15,23,42,.6) 100%)',backdropFilter:'blur(8px)',border:'1px solid rgba(59,130,246,.15)'}}>
        {([
          {k:'intelligence', label:'Intelligence Incidents', icon:'🧠'},
          {k:'gestion',      label:'Gestion des incidents',  icon:'📋'},
        ] as const).map(t=>(
          <button key={t.k} onClick={()=>setActiveTab(t.k)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeTab===t.k?'bg-blue-600 text-white shadow-lg':'text-slate-400 hover:text-white'}`}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>
      {activeTab==='intelligence' && !isOfficier && <IncidentsAnalyticsPageInner user={user}/>}
      {activeTab==='gestion'      && <IncidentBoardPage user={user}/>}
    </div>
  );
};
